import {Moment} from 'moment-timezone'

import {SLIDER_ROUND_TO_INTERVAL_HOURS} from '../constants'

import {Electricity} from './../interfaces/api/electricity'
import {MomentRange, NumberRange} from './../selectors/time'

export const getSubmitTimeFrame = (
  upForPurchasing?: Electricity['upForPurchasing']
): [submitStart: Moment | undefined, submitEnd: Moment | undefined] => {
  if (!upForPurchasing?.length) return [undefined, undefined]
  const start = upForPurchasing[0].dateTimeUTC
  const end = upForPurchasing[upForPurchasing.length - 1].dateTimeUTC
  return [start, end]
}

export const toPixel = (value: number): string => `${value}px`

export const calculateRangeWithinBoundary = (
  inputRange: [number, number],
  boundary: [number, number]
): [number, number] => {
  const [startRange, endRange] = inputRange
  const delta = endRange - startRange
  const [min, max] = boundary

  if (delta > max - min) {
    return boundary
  }
  if (startRange < min) {
    return [min, min + delta]
  }
  if (endRange > max) {
    return [max - delta, max]
  }
  return inputRange
}

/**
 * Function to calculate datetime range within boundary
 * diff is calculated in hours, minutes are not taken into account
 */
export const calculateDatetimeRangeWithinBoundary = (
  inputRange: MomentRange,
  boundary: MomentRange
): MomentRange => {
  const [startRange, endRange] = inputRange
  const delta = endRange.diff(startRange, 'hours')
  const [min, max] = boundary

  if (delta > max.diff(min, 'hours')) {
    return boundary
  }
  if (startRange.isBefore(min)) {
    return [min, min.clone().add(delta, 'hours')]
  }
  if (endRange.isAfter(max)) {
    return [max.clone().subtract(delta, 'hours'), max]
  }
  return inputRange
}

export const getMWhStepsForElectricityChart = (maxValue: number) => {
  let step = 1
  if (maxValue >= 100) step = 10
  else if (maxValue >= 40) step = 5
  else if (maxValue >= 11) step = 2

  const steps = Array.from({length: Math.ceil(maxValue / step)}, (_, index) => index * step)

  return steps
}

/** Function to transform array to a record by key */
export const toRecord = <T>(data: T[], property: keyof T): Record<string, T> => {
  return data.reduce(
    (acc, item) => {
      acc[String(item[property])] = item
      return acc
    },
    {} as Record<string, T>
  )
}

const adjustRangeByMinLimit = (range: MomentRange, minHours: number): MomentRange => {
  const [start, end] = range
  const rangeLenghtInHours = end.diff(start, 'hours')
  return rangeLenghtInHours < minHours ? [start, start.clone().add(minHours, 'hours')] : range
}
/**
 * function to adjust range by min max limit and boundary
 * used to sanitize url params for chart start and end
 */
export const adjustRangeByMinMaxLimitAndBoundary = (
  range: MomentRange,
  boundary: MomentRange,
  minMaxRangeHours: NumberRange
) => {
  const [minRangeHours, maxRangeHours] = minMaxRangeHours
  const rangeAdjustedByMinLimit = adjustRangeByMinLimit(range, minRangeHours)
  const rangeAdjustedByMaxLimit = calculateDatetimeRangeWithinBoundary(rangeAdjustedByMinLimit, [
    rangeAdjustedByMinLimit[0],
    rangeAdjustedByMinLimit[0].clone().add(maxRangeHours, 'hours')
  ])
  return calculateDatetimeRangeWithinBoundary(rangeAdjustedByMaxLimit, boundary)
}

/**
 * function to round date down to nearest 12 hours
 */
export function roundTimeDownToNearest12Hours(date: Moment) {
  const hours = date.hours()
  const roundedHours =
    Math.floor(hours / SLIDER_ROUND_TO_INTERVAL_HOURS) * SLIDER_ROUND_TO_INTERVAL_HOURS
  return date.clone().startOf('day').add(roundedHours, 'hours')
}

/**
 * function to round date up to nearest 12 hours
 */
export function roundTimeUpToNearest12Hours(date: Moment) {
  const hours = date.hours()
  const roundedHours =
    Math.ceil(hours / SLIDER_ROUND_TO_INTERVAL_HOURS) * SLIDER_ROUND_TO_INTERVAL_HOURS
  return date.clone().startOf('day').add(roundedHours, 'hours')
}

/**
 * Function to calculate the percentage difference between two numbers.
 * It returns a float or positive/negative infinity.
 * @param a The first number.
 * @param b The second number.
 * @returns The percentage difference between a and b.
 */
export const calculateDeltaPercentage = (a: number, b: number): number => {
  if (a === 0 && b > 0) {
    return Number.POSITIVE_INFINITY
  }
  if (a === 0 && b < 0) {
    return Number.NEGATIVE_INFINITY
  }
  if (a > 0 && b === 0) {
    return -100
  }
  if (a === b) {
    return 0
  }
  return ((b - a) / a) * 100
}
